/**
 * CodingSidebar — the rail for the Teammate Code surface (spec 083 D2).
 *
 * Coding replaces the platform sidebar rather than nesting inside it: stacking
 * two rails cost a third of the window to show two navigations at once, and the
 * app nav is not what you steer by while reading a diff. It is still the same
 * product, so this is built from the same Sidebar primitives and reuses the
 * platform header and footer verbatim (SidebarWorkspaceHeader /
 * SidebarFooterNav) — only the middle section differs: a way back out, a
 * new-task action, and the coding session history in place of the app nav and
 * chat list.
 *
 * The rail owns its own data. Both coding pages render inside it, so having
 * each of them fetch the session list and thread it down through the layout
 * only meant the same query written twice.
 *
 * @module components/coding/coding-sidebar
 */

import { Link, useLocation, useParams } from "react-router-dom"
import { ArrowLeft, FilePlus, GitPullRequest, Plus, Zap } from "lucide-react"

import { SidebarFooterNav } from "@/components/layout/sidebar/sidebar-footer-nav"
import { SidebarWorkspaceHeader } from "@/components/layout/sidebar/sidebar-workspace-header"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar"
import { Skeleton } from "@/components/ui/skeleton"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { groupSessionsByRepo } from "@/components/coding/sidebar/group-sessions"
import { ProjectGroup } from "@/components/coding/sidebar/project-group"
import { useCodeSessions } from "@/hooks/use-code-sessions"
import { useProjectTreeState } from "@/hooks/use-project-tree-state"

export function CodingSidebar() {
  const { appId, id } = useParams<{ appId: string; id: string }>()
  const { state } = useSidebar()
  const isCollapsed = state === "closed"

  const basePath = `/app/${appId}/coding`
  const { sessions, loading } = useCodeSessions(appId)
  const tree = useProjectTreeState(appId)

  /*
    Grouped by repository, which the rail can do on its own — every session
    carries `repoOwner`/`repoName`. Recomputed each render rather than memoised:
    the list is tens of rows and the query it comes from is already polled, so a
    memo would guard an operation cheaper than the comparison guarding it.
  */
  const projects = groupSessionsByRepo(sessions)

  /*
    "pull-requests" and "automations" arrive as `:id` on the sibling routes, so
    they have to be excluded explicitly — otherwise the rail highlights a
    session that does not exist and leaves the row for the page you are actually
    on looking inactive.
  */
  const pathname = useLocation().pathname
  const onPullRequests = pathname.endsWith("/coding/pull-requests")
  const onAutomations = pathname.endsWith("/coding/automations")

  /** No session in the URL means the new-task form is what is on screen. */
  const activeId = onPullRequests || onAutomations ? null : (id ?? null)

  return (
    <Sidebar collapsible="icon" className="border-r-0">
      <SidebarHeader className="p-3 group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:py-2">
        {/* No search: the ⌘K palette indexes chats and agents, neither of which
            this surface has. */}
        <SidebarWorkspaceHeader showSearch={false} />

        <SidebarMenu className="mt-2 group-data-[collapsible=icon]:hidden">
          {/* This sidebar REPLACES the platform sidebar, so returning to the
              rest of the product has to be an explicit affordance rather than
              the nav that is no longer on screen. Sits above the task action as
              the way back out, mirroring a breadcrumb. */}
          <SidebarMenuItem>
            <SidebarMenuButton asChild>
              <Link data-testid="coding-back-to-app" to={`/app/${appId}/chat`}>
                <ArrowLeft className="size-4" />
                <span>Back to main app</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={activeId === null && !onPullRequests}>
              <Link data-testid="coding-new-session" to={basePath}>
                <FilePlus className="size-4" />
                <span>New Task</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          {/* With "New Task" rather than in the tree below: a pull request is
              not a session until you review it, and both of these start
              something rather than return to it. */}
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={onPullRequests}>
              <Link data-testid="coding-pull-requests" to={`${basePath}/pull-requests`}>
                <GitPullRequest className="size-4" />
                <span>Pull requests</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
          {/* Configuration rather than a starting point, but it belongs beside
              the two things it configures — an automation IS a session someone
              else started. */}
          <SidebarMenuItem>
            <SidebarMenuButton asChild isActive={onAutomations}>
              <Link data-testid="coding-automations" to={`${basePath}/automations`}>
                <Zap className="size-4" />
                <span>Automations</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>

        {/* The header's own trigger hides in the icon rail, so without this a
            collapsed sidebar has no way back to expanded. Mirrors AppSidebar. */}
        <div className="hidden justify-center group-data-[collapsible=icon]:flex">
          <Tooltip>
            <TooltipTrigger asChild>
              <SidebarTrigger className="size-8" aria-label="Expand coding sidebar" />
            </TooltipTrigger>
            <TooltipContent side="right">Expand sidebar</TooltipContent>
          </Tooltip>
        </div>
      </SidebarHeader>

      {/* Session history — its own scrolling section, mirroring the chat list's
          place in the platform sidebar. */}
      <SidebarContent>
        <SidebarGroup className="py-1">
          <SidebarGroupLabel className="flex w-full items-center justify-between px-3 text-muted-foreground group-data-[collapsible=icon]:hidden">
            <span>Projects</span>
            <Link
              to={basePath}
              data-testid="coding-new-session-inline"
              className="flex items-center gap-1 rounded px-1 py-0.5 font-semibold text-primary transition-colors hover:bg-sidebar-accent"
            >
              <Plus className="size-3.5" />
              <span>New</span>
            </Link>
          </SidebarGroupLabel>
          <SidebarGroupContent>
            {loading && sessions.length === 0 ? (
              <SidebarMenu className="gap-1 group-data-[collapsible=icon]:hidden">
                {[1, 2, 3, 4].map((row) => (
                  <SidebarMenuItem key={row}>
                    <div className="flex items-center gap-3 px-2 py-2">
                      <Skeleton className="size-4 rounded" />
                      <Skeleton className="h-4 flex-1" />
                    </div>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            ) : sessions.length === 0 ? (
              <p className="px-3 py-2 text-sm text-muted-foreground group-data-[collapsible=icon]:hidden">
                No coding sessions yet. Start one with New Task.
              </p>
            ) : (
              <SidebarMenu className="group-data-[collapsible=icon]:hidden">
                {projects.map((project) => (
                  <ProjectGroup
                    key={project.key}
                    group={project}
                    basePath={basePath}
                    activeId={activeId}
                    collapsed={tree.isCollapsed(project.key)}
                    onToggleCollapsed={() => tree.toggleCollapsed(project.key)}
                    expanded={tree.isExpanded(project.key)}
                    onToggleExpanded={() => tree.toggleExpanded(project.key)}
                  />
                ))}
              </SidebarMenu>
            )}
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/*
        Profile only, and no rule above it.

        Settings / Linked Accounts / Help were carried over so this surface did
        not lose account access when it replaced the platform sidebar — but the
        rail already opens with "Back to main app", which is where all three
        live, and repeating them under a session tree spent the footer on
        navigation away from the thing being navigated. The border went with
        them: it was separating the tree from a menu that is no longer there,
        and the profile card's own outline is already its edge.
      */}
      <SidebarFooter className="mt-2 gap-1 px-3 pb-3 pt-3 group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:py-2">
        <SidebarFooterNav collapsed={isCollapsed} navHidden />
      </SidebarFooter>
    </Sidebar>
  )
}

CodingSidebar.displayName = "CodingSidebar"
